import { Box } from '@mui/material';
import { createContext, ReactNode, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { formatPatientName } from '~/containers/patient-list/model';
import { readSelectedPatient } from '~/hooks/patient/useSelectedPatient';
import useParkedQuestion from '~/hooks/useParkedQuestion';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import {
    pseudonymizeState,
    showSensitiveInfoState,
} from '~/state/atoms/viewerAtoms';

/** How one sending of patient data goes, once the user agreed to it. */
export type DeidentificationChoice = { readonly deidentify: boolean };

/**
 * Resolves how one sending of patient data goes, or `undefined` when the user
 * declined to send it. An object, so `deidentify` cannot be read, nor the
 * answer passed as a boolean, before the decline is ruled out.
 */
export type ChooseDeidentification = () => Promise<
    DeidentificationChoice | undefined
>;

/** {@link ChooseDeidentification}, declined once `asker` aborts. */
export type ChooseDeidentificationFor = (
    asker: AbortSignal
) => ReturnType<ChooseDeidentification>;

export const DeidentificationChoiceContext =
    createContext<ChooseDeidentificationFor | null>(null);

type Answer = 'deidentify' | 'sendIdentifiable' | 'decline';

/** The patient whose data would be sent, as the question names it. */
type IdentifiableSendQuestion = {
    /** The DICOM Person Name, as stored. */
    readonly patientName: string;
    /** The selected patient, as `selectedPatientIdState` holds it. */
    readonly patientId: string | null;
};

const NO_QUESTION: IdentifiableSendQuestion = {
    patientName: '',
    patientId: null,
};

/**
 * Decides, for each sending of patient data to the cloud backend, whether it
 * is de-identified. With "De-identify data" on it is, without a question.
 * With it off the user is asked first, with the selected patient named:
 * "Send identifiable data" sends identifiable data, and "De-identify and
 * send" turns "De-identify data" back on and sends de-identified data.
 * Cancel, Escape, a newer question, another selected patient, the asker
 * aborting and unmounting all decline; a click outside the dialog does not.
 * Cancel has the focus when the question opens. While patient information is
 * hidden the patient cannot be read, so identifiable data cannot be sent.
 */
export default function DeidentificationChoiceProvider({
    children,
}: {
    children: ReactNode;
}) {
    const { question, ask, answer } = useParkedQuestion<
        IdentifiableSendQuestion,
        Answer
    >(NO_QUESTION, 'decline');
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const patientInfoShown = useRecoilValue(showSensitiveInfoState);

    useEffect(() => {
        if (question.open && question.patientId !== selectedPatientId) {
            answer('decline');
        }
    }, [question, selectedPatientId, answer]);

    const choose = useRecoilCallback(
        ({ snapshot, reset }): ChooseDeidentificationFor =>
            async (asker) => {
                if (snapshot.getLoadable(pseudonymizeState).getValue()) {
                    return { deidentify: true };
                }
                const choice = await ask(
                    {
                        patientName:
                            readSelectedPatient(snapshot)?.dataSet
                                .PatientName ?? '',
                        patientId: snapshot
                            .getLoadable(selectedPatientIdState)
                            .getValue(),
                    },
                    asker
                );
                if (choice === 'deidentify') {
                    reset(pseudonymizeState);
                    return { deidentify: true };
                }
                return choice === 'sendIdentifiable'
                    ? { deidentify: false }
                    : undefined;
            },
        [ask]
    );

    return (
        <DeidentificationChoiceContext.Provider value={choose}>
            {children}
            <ConfirmationDialog
                open={question.open}
                title={<FormattedMessage id="identifiableSend.title" />}
                message={
                    <>
                        <FormattedMessage id="identifiableSend.message" />
                        <Box component="span" display="block" mt={1}>
                            <FormattedMessage
                                id="identifiableSend.patient"
                                values={{
                                    name: (
                                        <SensitiveInfoTypography>
                                            {formatPatientName(
                                                question.patientName
                                            ) || (
                                                <FormattedMessage id="unknown" />
                                            )}
                                        </SensitiveInfoTypography>
                                    ),
                                    id: (
                                        <SensitiveInfoTypography>
                                            {question.patientId || (
                                                <FormattedMessage id="unknown" />
                                            )}
                                        </SensitiveInfoTypography>
                                    ),
                                }}
                            />
                        </Box>
                        {!patientInfoShown && (
                            <Box component="span" display="block" mt={1}>
                                <FormattedMessage id="identifiableSend.patientInfoHidden" />
                            </Box>
                        )}
                    </>
                }
                confirmText={<FormattedMessage id="identifiableSend.confirm" />}
                confirmDisabled={!patientInfoShown}
                cancelOnOutsideClick={false}
                onConfirm={() => answer('sendIdentifiable')}
                onCancel={() => answer('decline')}
                extraAction={{
                    label: (
                        <FormattedMessage id="identifiableSend.deidentify" />
                    ),
                    onClick: () => answer('deidentify'),
                    testId: 'identifiable-send-deidentify',
                }}
            />
        </DeidentificationChoiceContext.Provider>
    );
}
